import { useQuery } from '@tanstack/react-query';
import { CheckSquare } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';

import { apiClient } from '@/lib/api-client';
import { cn } from '@/lib/utils';
import { CardShell, EmptyState } from './card-shell';

function formatFatal(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
  });
}

function urgencyClass(iso: string | null): string {
  if (!iso) return 'text-[var(--fg-muted)]';
  const days = (new Date(iso).getTime() - Date.now()) / (1000 * 60 * 60 * 24);
  if (days < 0) return 'text-red-700 font-semibold';
  if (days <= 3) return 'text-red-600 font-medium';
  if (days <= 7) return 'text-amber-600';
  return 'text-[var(--fg-muted)]';
}

/**
 * Widget de tarefas pendentes do dashboard.
 *
 * Busca minhas tarefas favoritadas pendentes (pinned=true, status=pending,
 * assigneeUserId=me) ordenadas por prazo fatal ascendente. Mostra ate 5.
 * Click em uma linha abre o drawer da tarefa via query string em
 * /escritorio/atividades?taskId=<id>.
 *
 * Estado vazio: mensagem positiva. Estado de loading: skeleton.
 */
export function PendingTasksCard() {
  const navigate = useNavigate();
  const { data, isLoading, isError } = useQuery({
    queryKey: ['tasks', 'list', 'pending-widget'],
    queryFn: () =>
      apiClient.tasksList({
        assigneeUserId: 'me',
        status: 'pending',
        pinned: true,
        pageSize: 5,
        orderBy: 'fatalDate',
        orderDir: 'asc',
      }),
    staleTime: 1000 * 30,
  });

  if (isLoading) {
    return (
      <CardShell title="Tarefas pendentes" icon={CheckSquare}>
        <div className="flex w-full flex-col gap-2">
          {Array.from({ length: 4 }).map((_, i) => (
            <div
              key={i}
              className="h-8 w-full animate-pulse rounded bg-[var(--surface-muted)]"
            />
          ))}
        </div>
      </CardShell>
    );
  }

  if (isError) {
    return (
      <CardShell title="Tarefas pendentes" icon={CheckSquare}>
        <p className="text-xs text-[var(--fg-muted)]">
          Não foi possível carregar.
        </p>
      </CardShell>
    );
  }

  const items = data?.items ?? [];

  if (items.length === 0) {
    return (
      <CardShell title="Tarefas pendentes" icon={CheckSquare}>
        <EmptyState
          icon={CheckSquare}
          message="Nenhuma tarefa pendente. Bom trabalho!"
          cta={{ label: 'Ver atividades', href: '/escritorio/atividades' }}
        />
      </CardShell>
    );
  }

  return (
    <CardShell title="Tarefas pendentes" icon={CheckSquare}>
      <div className="flex w-full flex-col gap-1">
        <ul className="flex flex-col divide-y divide-[var(--border)]">
          {items.map((t) => (
            <li key={t.id}>
              <button
                type="button"
                onClick={() =>
                  navigate(
                    `/escritorio/atividades?taskId=${encodeURIComponent(t.id)}`,
                  )
                }
                className={cn(
                  'flex w-full items-center justify-between gap-3 rounded px-1 py-1.5 text-xs transition',
                  'hover:bg-[var(--surface-muted)]',
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--accent)]',
                )}
              >
                <div className="flex min-w-0 items-center gap-2">
                  {t.category && (
                    <span
                      className="inline-block h-2 w-2 shrink-0 rounded-full"
                      style={{ background: t.category.color }}
                      aria-hidden
                    />
                  )}
                  <span className="truncate text-[var(--fg)]">{t.title}</span>
                </div>
                <span
                  className={cn(
                    'shrink-0 text-[10px]',
                    urgencyClass(t.fatalDate),
                  )}
                >
                  {formatFatal(t.fatalDate)}
                </span>
              </button>
            </li>
          ))}
        </ul>
        <Link
          to="/escritorio/atividades"
          className={cn(
            'mt-2 inline-flex items-center justify-center gap-1 rounded-md px-3 py-1.5 text-xs font-medium transition',
            'border border-[var(--border)] text-[var(--fg)] hover:bg-[var(--surface-muted)]',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--accent)]',
          )}
        >
          Ver todas as tarefas
        </Link>
      </div>
    </CardShell>
  );
}
